import { AppConfig } from '@app/app.config';
import { Injectable } from '@angular/core';
import { HttpOptionsService, HttpService } from '@app/security/shared/http.service';
import { Logger } from '@app/logger/logger';
import { LoginStatusModel } from '@app/security/shared/login-status-change.model';
import { LoginStatusService } from '@app/security/shared/login-status.service';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ConnectionInfo, IConnectionInfo } from '@app/server/models/connection-info.model';
import { TokenLoginRaw } from '@app/security/token-login.model';

const lgSfx = '\t(auth.service.js)';

@Injectable({ providedIn: 'root' })
export class AuthService {
  public expires: number;
  public token: string;
  public tokenType: string;

  constructor(
    private http: HttpService,
    private config: AppConfig,
    private log: Logger,
    private loginStatusService: LoginStatusService,
    private optionsService: HttpOptionsService
  ) {
    this.loginStatusService.loginStatus.subscribe((loginStatus: LoginStatusModel) => {
      if (loginStatus.loggedIn === false) {
        this.cleanCache();
      }
    });
  }

  /***
   * This method should only be used during development to make it easy to
   * quickly reload a view. It is not ready or tested to be used to login
   * a production user. Of potential concern is that when using a token we
   * have no way of setting the username or database values for use
   * elsewhere in the system.
   */
  public authenticateWithTokenInfo(
    newToken: string,
    newTokenType: string,
    newExpires: number
  ): Observable<IConnectionInfo> {
    this.expires = newExpires;
    this.token = newToken;
    this.tokenType = newTokenType;

    this.optionsService.setAuthorization(`${this.tokenType} ${this.token}`);

    const API_ENDPOINT = '/api/v1/connection';
    const endpoint = this.config.getServerUri() + API_ENDPOINT;

    return this.http.get<IConnectionInfo>(endpoint, ConnectionInfo);
  }

  public authenticateWithCurrentTokenInfo(): Observable<boolean> {
    return this.authenticateWithTokenInfo(this.token, this.tokenType, this.expires).pipe(
      map((result) => {
        return (
          result.database &&
          result.database.trim().length > 0 &&
          result.username &&
          result.username.trim().length > 0
        );
      })
    );
  }

  public authenticate(
    userId: string,
    password: string,
    database: string,
    mqServer
  ): Observable<boolean> {
    if (!userId || !database) {
      return throwError('Please provide a user ID and select a database.');
    }

    const requestData = {
      client_id: userId,
      client_secret: password,
      grant_type: 'client_credentials'
    };

    const API_ENDPOINT = '/token';
    const endpoint = this.config.getServerUri() + API_ENDPOINT;
    const body = this.transformRequest(requestData);

    const options = this.optionsService.get();
    options.headers = options.headers.set('Content-Type', 'application/x-www-form-urlencoded');
    options.headers = options.headers.set('database', database);
    options.headers = options.headers.set('mqServer', mqServer);

    return this.http.postAny(endpoint, body, options).pipe(
      map((resp: TokenLoginRaw) => {
        this.token = resp.access_token;
        this.expires = resp.expires_in;
        this.tokenType = resp.token_type;

        if (this.token) {
          this.log.info(`User login success ${lgSfx}`);
          return true;
        } else {
          this.log.warn(`User login failed. Status: ${status} ${lgSfx}`);
          return false;
        }
      }),
      catchError((err) => {
        const error =
          err && err.data && err.data.error_description ? err.data.error_description : undefined;
        this.log.warn(`User login failed. Status: ${err.status}  Message: ${error} ${lgSfx}`);
        return throwError(
          error || 'The username and password are not valid. Please re-enter them and try again.'
        );
      })
    );
  }

  private cleanCache(): void {
    this.expires = null;
    this.token = null;
    this.tokenType = null;
  }

  private transformRequest(obj: any): string {
    const str = [];
    for (const p in obj) {
      if ({}.hasOwnProperty.call(obj, p)) {
        str.push(encodeURIComponent(p) + '=' + encodeURIComponent(obj[p]));
      }
    }
    return str.join('&');
  }
}
